import { Flex, Box, Text } from '@chakra-ui/react';
import { ReactComponent as PersonIcon } from 'assets/person-icon.svg';
import { ReactComponent as SignedIcon } from 'assets/signed-icon.svg';
import { ReactComponent as PendingIcon } from 'assets/pending-icon.svg';
import { ReactComponent as NotSignedIcon } from 'assets/not-signed-icon.svg';
import { TransactionStatusEnum } from '../../../../types/transactions';
import { useState } from 'react';

interface Signer {
  accountId: string;
  name?: string;
  key: string;
}

interface Signature {
  transactionId: string;
  key: string;
  accountId: string;
}

interface SignerGroup {
  label: string;
  threshold: number;
  signers: Signer[];
}

interface ProgressTimelineProps {
  createdAt: string;
  status: number;
  statusColor?: string;
  signaturesCount: number;
  requiredSignatures: number;
  signatures: Signature[];
  signers: Signer[];
  expiresAt: number;
  isScreenSmall: boolean;
  /** For ThresholdUpdate: split into old/new signer groups */
  signerGroups?: SignerGroup[];
}

const SHOW_ALL_THRESHOLD = 8;

const SignerRow = ({
  signer,
  hasSigned,
  status,
  isScreenSmall,
}: {
  signer: Signer;
  hasSigned: boolean;
  status: number;
  isScreenSmall: boolean;
}) => {
  const signerLabel = signer.name
    ? `${signer.name} (${signer.accountId})`
    : signer.accountId;

  if (hasSigned) {
    return (
      <Flex alignItems="center" gap={1}>
        <SignedIcon style={{ flexShrink: 0 }} />
        <Text size="xs" color="blackAlpha.700" isTruncated>
          {signerLabel}
        </Text>
        {!isScreenSmall && (
          <Text whiteSpace="nowrap" size="xs" color="blackAlpha.700">
            - Signed
          </Text>
        )}
      </Flex>
    );
  }

  if (status === TransactionStatusEnum.Waiting || status === TransactionStatusEnum.AwaitingExecution) {
    return (
      <Flex alignItems="center" gap={1}>
        <PendingIcon style={{ flexShrink: 0 }} />
        <Text size="xs" color="blackAlpha.700" isTruncated>
          {signerLabel}
        </Text>
        {!isScreenSmall && (
          <Text whiteSpace="nowrap" size="xs" color="blackAlpha.700">
            - Pending
          </Text>
        )}
      </Flex>
    );
  }

  return (
    <Flex alignItems="center" gap={1}>
      <NotSignedIcon style={{ flexShrink: 0 }} />
      <Text size="xs" color="blackAlpha.400" isTruncated>
        {signerLabel}
      </Text>
      {!isScreenSmall && (
        <Text whiteSpace="nowrap" size="xs" color="blackAlpha.400">
          - Not signed
        </Text>
      )}
    </Flex>
  );
};

const hasSigned = (signer: Signer, signatures: Signature[]) =>
  signatures?.some(
    (sig) =>
      (sig.accountId && sig.accountId === signer.accountId) ||
      sig.key === signer.key,
  );

const ProgressTimeline = ({
  createdAt,
  status,
  statusColor,
  signaturesCount,
  requiredSignatures,
  signatures,
  signers,
  expiresAt,
  isScreenSmall,
  signerGroups,
}: ProgressTimelineProps) => {
  const [showAll, setShowAll] = useState(false);
  const isSplitView = signerGroups && signerGroups.length > 0;

  const displaySigners = showAll || signers.length <= SHOW_ALL_THRESHOLD
    ? signers
    : signers.slice(0, SHOW_ALL_THRESHOLD);

  const renderSignerList = () => {
    if (isSplitView) {
      return (
        <Box
          py={3}
          pl={isScreenSmall ? 3 : 4}
          pr={0}
          mb={4}
          gap={4}
          display="flex"
          flexDirection="column"
        >
          {signerGroups.map((group) => {
            const groupSigned = group.signers.filter((s) => hasSigned(s, signatures)).length;
            return (
              <Box key={group.label}>
                <Flex alignItems="center" gap={1} mb={2}>
                  <Text size="xs" variant="bodyBold">
                    {group.label}
                  </Text>
                  <Text size="xs" color="blackAlpha.500">
                    ({groupSigned} of {group.threshold} required)
                  </Text>
                </Flex>
                <Flex direction="column" gap={2} pl={2}>
                  {group.signers.map((signer) => (
                    <SignerRow
                      key={`${group.label}-${signer.accountId}`}
                      signer={signer}
                      hasSigned={hasSigned(signer, signatures)}
                      status={status}
                      isScreenSmall={isScreenSmall}
                    />
                  ))}
                </Flex>
              </Box>
            );
          })}
        </Box>
      );
    }

    return (
      <Box
        py={3}
        pl={isScreenSmall ? 3 : 4}
        pr={0}
        mb={4}
        gap={2}
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
      >
        {displaySigners.map((signer: Signer) => (
          <SignerRow
            key={signer.accountId}
            signer={signer}
            hasSigned={hasSigned(signer, signatures)}
            status={status}
            isScreenSmall={isScreenSmall}
          />
        ))}
        {signers.length > SHOW_ALL_THRESHOLD && !showAll && (
          <Text
            as="button"
            size="xs"
            color="blue.500"
            cursor="pointer"
            textAlign="left"
            _hover={{ textDecoration: 'underline' }}
            onClick={(e) => {
              e.stopPropagation();
              setShowAll(true);
            }}
          >
            Show all {signers.length} signers
          </Text>
        )}
      </Box>
    );
  };

  return (
    <Flex direction="column" gap={1}>
      <Text size="xs">Progress</Text>
      <Box
        p={isScreenSmall ? 4 : 6}
        display="flex"
        bg="white"
        borderRadius={8}
        gap={4}
      >
        {/* Timeline dots + connecting line */}
        <Flex direction="column" alignItems="center">
          {/* Created dot */}
          <Box
            w={4}
            h={4}
            bg="white"
            borderRadius="full"
            border="2px solid rgba(0, 0, 0, 0.36)"
            flexShrink={0}
          />
          {/* Line to Signed */}
          <Box flex={0} borderLeft="2px solid" borderColor="gray.200" minH="16px" h="16px" />
          {/* Signed dot */}
          <Box
            w="14px"
            h="14px"
            bg="white"
            borderRadius="full"
            border="2px solid rgba(0, 0, 0, 0.36)"
            flexShrink={0}
          />
          {/* Line to Status — grows with signer list */}
          <Box flex={1} borderLeft="2px solid" borderColor="gray.200" minH="24px" />
          {/* Status dot */}
          <Box w={4} h={4} bg={statusColor} borderRadius="full" flexShrink={0} />
        </Flex>

        {/* Content */}
        <Flex flex={2} direction="column">
          {/* Created */}
          <Box display="flex" alignItems="center" gap={1}>
            <Text size="xs" variant="bodyBold">
              Created
            </Text>
            <Text
              size="xs"
              whiteSpace="nowrap"
              sx={{ ...(isScreenSmall ? { fontSize: '11px !important' } : {}) }}
            >
              {new Date(createdAt).toLocaleString(undefined, {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                hour12: false,
                minute: '2-digit',
                second: '2-digit',
              })}
            </Text>
          </Box>

          {/* Signed header */}
          <Box mt={4} display="flex" alignItems="center" gap={2}>
            <Text size="xs" variant="bodyBold">
              Signed
            </Text>
            <PersonIcon />
            <Text size="xs">
              {signaturesCount} signed out of {requiredSignatures} required
            </Text>
          </Box>

          {/* Signer list (flat or split) */}
          {renderSignerList()}

          {/* Final status */}
          <Box display="flex" alignItems="center" gap={1}>
            {status === TransactionStatusEnum.Waiting && (
              <Text size="xs" variant="bodyBold">
                Pending
              </Text>
            )}
            {status === TransactionStatusEnum.Expired && (
              <Text size="xs" variant="bodyBold">
                Expired
              </Text>
            )}
            {status === TransactionStatusEnum.AwaitingExecution && (
              <Flex direction="column" gap={1}>
                <Text size="xs" variant="bodyBold" color="blue.600">
                  To be executed
                </Text>
                <Text size="xs" color="blackAlpha.600">
                  at{' '}
                  {new Date(expiresAt * 1000).toLocaleString(undefined, {
                    day: '2-digit',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Text>
              </Flex>
            )}
            {status === TransactionStatusEnum.Executed && (
              <Text size="xs" variant="bodyBold" color="teal.600">
                Executing...
              </Text>
            )}
            {status === TransactionStatusEnum.Success && (
              <Text size="xs" variant="bodyBold">
                Executed
              </Text>
            )}
            {status === TransactionStatusEnum.Failed && (
              <Text size="xs" variant="bodyBold" color="red.500">
                Failed
              </Text>
            )}
          </Box>
        </Flex>
      </Box>
    </Flex>
  );
};

export default ProgressTimeline;
